import { sem_imagem as semImg } from '@/constants';
import { useClient, useFetchAxios, useGlobals, useUserContext } from '@/hooks';
import { getOnStorage, removeFromStorage, setOnStorage } from '@/shared';
import { unregisterServiceWorker } from '@/shared/serviceWorkFunctions';
import {
  CampanhaProdutosProps,
  CategoryProps,
  CustomerDataProps,
  DataSetsProps,
  DicUltimaCompraProps,
  EventsClientProps,
  PrecoPromocaoClienteProps,
  ProductProps,
  PromocaoClientProps,
  tableKeys,
  TableTypes,
  UltimaCompraProps
} from '@/types';
import { useCallback, useRef, useState } from 'react';
import { Tables, toCoded, useDbFunctions } from '../dbFunctions/useDBFunctions';

type CatalogDictProps = Record<number, ProductProps>;
type CategoryDictProps = Record<number, CategoryProps>;

const updateProgress = (loaded: number, total: number, name: string) => {
  const percentual = Math.min(((loaded / total) * 100), 100);
  const progressBar = document.getElementById(`${name}-bar`) as HTMLElement;
  const progressText = document.getElementById(`${name}-text`) as HTMLElement;
  const isInfinity = percentual !== Infinity;

  if (progressBar) progressBar.style.width = `${percentual}%`;
  if (progressText) {
    progressText.textContent = isInfinity ? `${Math.round(percentual)}%` : 'Erro no Download';
  }
};

export const useDownloadFiles = () => {
  const loading = useRef(false);

  const [hasError, setError] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [fileName, setFileDownloading] = useState('');
  const [isOpenedModal, openModalSync] = useState(false);
  const [noUpdate, setNoUpdate] = useState(false as Boolean);
  const [datasets, setDataSets] = useState([] as DataSetsProps[]);

  const { axiosCtx } = useFetchAxios();
  const { setCustomer } = useUserContext();
  const { setLastSync, getDate } = useGlobals();
  const { setOnDB, getOnDB } = useDbFunctions();
  const { setCampanhaProdutos, setCampanhaClientes, setCampanhaFaixa, setCampanhaTopMix, setIndicadorLoja, setIndicadoresProdutividade } = useClient();

  const setDownloadingFileName = useCallback(() => {
    if (isSuccess) return;

    const listDataBase = getOnStorage('datasets');
    const ind = Math.floor(Math.random() * (listDataBase?.length || 0));
    let label = listDataBase?.[ind]?.DESC_ARQUIVO;
    if (label === fileName) {
      label = listDataBase?.[ind + 1]?.DESC_ARQUIVO || 'Baixando';
    }

    setFileDownloading(label || '');
    setTimeout(() => setDownloadingFileName(), 3000);
  }, [isSuccess, fileName]);

  const fetchDatasets = useCallback(async (list?: DataSetsProps[]) => {
    var totalSize = 0;
    var downloadedSize = 0; // bytes

    const fetchAndSaveJson = async (url: string, name: string, enableLog?: boolean): Promise<any> => {
      return new Promise(async (resolve, reject) => {
        try {
          const resp = await fetch(url);
          if (resp.status >= 300 || resp.status < 200) {
            throw new Error(`Failed to fetch ${name}: ${resp.statusText}`);
          }
          
          var totalArchive = 0;
          var downloadedArchive = 0;

          totalSize += Number(resp.headers.get('content-length'));
          totalArchive += Number(resp.headers.get('content-length'));

          let chunks: any[] = [];
          const reader = resp?.body?.getReader();
          const decoder = new TextDecoder('utf-8');

          const readChunk = async () => {
            try {
              const result = await reader?.read();
              if (!result) {
                throw new Error('Failed to read from stream');
              }
              const { done, value } = result;
              if (done) {
                const json = JSON.parse(chunks.join(''));
                let data = json?.catalogo || json?.preco || json;

                // console.log(`Arquivo baixado: ${name}`, json);

                if (name === 'imagens') {
                  data = typeof data === 'string' ? JSON.parse(data) : data;
                  data = data?.reduce(
                    (acc: any, item: any) => ({
                      ...acc,
                      [item.COD_PRODUTO]: item.imagem,
                    }),
                    {}
                  );
                }

                if (typeof data === 'string') data = JSON.parse(data);
                resolve({ data, name, enableLog });
                return;
              }

              downloadedSize += value?.byteLength || 1;
              downloadedArchive += value?.byteLength || 1;
              updateProgress(downloadedArchive, totalArchive, name);
              updateProgress(downloadedSize, totalSize, 'progress');
              if (value?.byteLength) {
                chunks.push(decoder.decode(value, { stream: true }));
                readChunk();
              }
            } catch (error) {
              console.error(`Error reading chunk for ${name}:`, error);
              reject(error);
            }
          };

          readChunk();
        } catch (error) {
          console.error(`Error fetching ${name}:`, error);
          setError(true);
          reject(error);
        }
      });
    };

    const links = list?.map(({ TIPO_ARQUIVO, URL_S3 }) => fetchAndSaveJson(URL_S3, TIPO_ARQUIVO, true)) || [];

    const response = await Promise.allSettled(links);

    const {fulfilled, rejected} = response.reduce((acc, item) => {
      if (item.status === "fulfilled") {
        acc.fulfilled[item.value.name] = item.value;
      }
      if (item.status === "rejected") {
        acc.rejected.push(item.reason);
      }
      return acc;
    }, { fulfilled: {}, rejected: [] } as { fulfilled: Record<string, any>, rejected: any[] });

    if (rejected.length > 0) {
      console.warn("Promises rejeitadas:", rejected);
    }

    return fulfilled || undefined;

  }, []);

  const loadFiles = useCallback(
    async (newDatasets?: DataSetsProps[]) => {
      if (loading.current) return;

      setDownloadingFileName();
      loading.current = true;
      setError(false);
      setIsSuccess(false);
      updateProgress(0, 0, '.');

      // A lista de datasets a serem baixados é passada diretamente.
      const listToDownload = newDatasets || [];
      if (!listToDownload.length) {
        setNoUpdate(true);
        loading.current = false;
        return;
      }
      
      if (listToDownload.find(i => i.TIPO_ARQUIVO === 'estoque')) {
        removeFromStorage('stock_left');
      }

      const resp = await fetchDatasets(listToDownload);
      
      // Pega a lista antiga de datasets
      const oldDatasets = getOnStorage('datasets') || [];
      // Pega os datasets que foram baixados com sucesso
      const successfullyDownloaded = listToDownload.filter(d => resp && resp[d.TIPO_ARQUIVO]);
      
      // Cria um mapa da lista antiga para fácil substituição
      const oldDatasetsMap = oldDatasets.reduce((acc, item) => {
        acc[item.TIPO_ARQUIVO] = item;
        return acc;
      }, {} as Record<string, DataSetsProps>);

      // Atualiza o mapa com os arquivos baixados com sucesso
      successfullyDownloaded.forEach(item => {
        oldDatasetsMap[item.TIPO_ARQUIVO] = item;
      });

      // Converte o mapa de volta para uma lista e salva
      const finalDatasetList = Object.values(oldDatasetsMap);
      setOnStorage('datasets', finalDatasetList);
      setOnStorage('new_data_sets', finalDatasetList);
      setDataSets(listToDownload);

      setPreparing(true);

      const getData = async <T extends Tables>(dbKey: T, defaultValue: any): Promise<TableTypes[T]> => {
        return resp?.[dbKey]?.data || await getOnDB(dbKey) || defaultValue;
      }

      const precos = await getData('preco', []);
      const marcas = await getData('marcas', []);
      const imagens = await getData('imagens', {});
      const estoque = await getData('estoque', []);
      const catalogo = await getData('catalogo', []);
      const saldoFlex = await getData('saldo_flex', []);
      const loadedClients = await getData('cliente', []);
      const categorias = await getData('categoria', []);
      const clienteBoletos = await getData('cliente_boletos', {});
      const clienteEventosCortes = await getData('cliente_cortes', {});
      const indicadores = await getData('indicadores', []);
      const campanha = await getData('campanha_indicador', []);
      const listPageBuilder = await getData('pagebuilder', []);
      const janelaEntrega = await getData('janela_entrega', []);
      const parametrosAws = await getData('parametros_aws', []);
      const indicadorLoja = await getData('indicador_loja', []);
      const campanhaFaixa = await getData('campanha_faixa', []);
      const campanhaProduto = await getData('campanha_produto', {});
      const campanhaCliente = await getData('campanha_cliente', {});
      const campanhaTopMix = await getData('campanha_mix_fornecedor', []);
      const parametroPrecoBase = await getData('parametro_preco_base', []);
      const recomendacaoBigData = await getData('recomendacaoBigData', []);
      const precoPromocaoCliente = await getData('precoPromocaoCliente', []);
      const cicloVendaCliente = await getData('ciclo_venda_cliente_cliente', []);
      const percentualFornecedor = await getData('percentual_fornecedor', []);
      const substituicaotributaria = await getData('cod_regime_tributario', []);
      const restricaoProdutoCliente = await getData('restricaoProdutoCliente', []);
      const ultimaCompra = await getData('ultima_compra', []);

      const eventos: EventsClientProps = clienteBoletos?.clientes?.reduce((acc, cliente) => {
        return {
          ...acc,
         [cliente.cod_cliente]: {
           boletos: cliente.boletos,
          cod_cliente: cliente.cod_cliente,
         }
        }
      }, {} as EventsClientProps) || {};

      const objCategory = {} as CategoryDictProps;

      const parseJSON = ((item: any) => {
        try {
          return JSON.parse(item)
        } catch (error) {
          return item
        }
      })

      // @TODO: Verificar se é necessário ordenar os clientes
      const clientes = loadedClients?.sort((a, b) => {
        if (a.NM_FANTASIA > b.NM_FANTASIA) return 1
        if (a.NM_FANTASIA < b.NM_FANTASIA) return -1
        return 0
      }).map(c => {
        // @TODO: Verificar sobre o parseJSON
        c.COD_SEGMENTO = parseJSON(c?.COD_SEGMENTO || '[]')
        // @TODO: Verificar sobre o parseJSON
        c.DT_ULTIMA_COMPRA = parseJSON(c?.DT_ULTIMA_COMPRA || '[]')
        // @TODO: Verificar sobre a flag unilever
        c.UNILEVER = c.FL_UNILEVER === 'True'
        const CYCLE = (cicloVendaCliente.filter(cycle => {
          return c.COD_CLIENTE === cycle.COD_CLIENTE
        }) || []).sort((a, b) => {
          return a.SEMANA_CICLO_ID - b.SEMANA_CICLO_ID
        })

        return { ...c, CYCLE }
      }) || [];

      const mapedPrices = precos.reduce((acc: any, i: any) => {
        if (!acc[i.COD_PRODUTO]) {
          acc[i.COD_PRODUTO] = []
        }
        acc[i.COD_PRODUTO].push(i)
        return acc
      }, {})

      const promocaoCliente = Array.isArray(precoPromocaoCliente) ? precoPromocaoCliente.reduce((acc: PrecoPromocaoClienteProps, p: PromocaoClientProps) => {
        const clientPrice = acc[p.COD_CLIENTE]
        if (!clientPrice) {
          acc[p.COD_CLIENTE] = { [p.QTD_EMBALAGEM]: { [p.COD_PRODUTO]: p } }
          return acc
        }

        const qtdEmbalagem = clientPrice[p.QTD_EMBALAGEM]
        if (!qtdEmbalagem) {
          acc[p.COD_CLIENTE][p.QTD_EMBALAGEM] = { [p.COD_PRODUTO]: p }
          return acc
        }

        const productPrice = qtdEmbalagem[p.COD_PRODUTO]
        if (!productPrice) {
          acc[p.COD_CLIENTE][p.QTD_EMBALAGEM][p.COD_PRODUTO] = p
          return acc
        }

        if (p.VLR_PRECO < productPrice.VLR_PRECO) {
          acc[p.COD_CLIENTE][p.QTD_EMBALAGEM][p.COD_PRODUTO] = p
          return acc
        }

        return acc
      }, {}) : precoPromocaoCliente;

      const productUltimaCompra = ultimaCompra?.reduce((acc: DicUltimaCompraProps, c: UltimaCompraProps) => {
        return { ...acc, [c.SEQPRODUTO]: c }
      }, {} as DicUltimaCompraProps);

      const {products, objProduct} = catalogo.reduce((acc: {products: ProductProps[], objProduct: CatalogDictProps}, item: ProductProps) => {
        const PRECO = mapedPrices[item.COD_PRODUTO];
        if (!PRECO?.length) return acc;

        const EMBALAGEM = item?.EMBALAGEM?.map((i) => {
          if (i?.QTD_MULTIPLO_VENDA > 1) {
            return { ...i, SG_EMBALAGEM: "PK" }
          }
          return { ...i }
        });
        const IMAGEM = imagens?.[item?.COD_PRODUTO] || imagens['sem_imagem'] || semImg;

        const product = {
          ...item, 
          PRECO, 
          IMAGEM, 
          EMBALAGEM, 
          UNILEVER: item.FL_UNILEVER === 'True',
          ultimaCompra: productUltimaCompra[item.COD_PRODUTO]
        }

        return {
          products: [...acc.products, product],
          objProduct: {...acc.objProduct, [item.COD_PRODUTO]: product}
        }
      }, {products: [], objProduct: {}} as {products: ProductProps[], objProduct: CatalogDictProps})

      if (!!categorias?.length) {
        categorias?.forEach((item: CategoryProps) => {
          if (!objCategory[item?.COD_CATEGORIA]) {
            objCategory[item?.COD_CATEGORIA] = {
              ...item,
            };
          }
        });
      }

      const mountCategories = (campanha: CampanhaProdutosProps) => {
        if (!campanha) return []
        
        return [{
          NRO_HIERARQUIA: 1,
          DE_CATEGORIA: campanha.nome,
          IMAGE: campanha?.inativo?.selo || '',
          PATH_CATEGORIA: campanha.url_amigavel,
        }] as CategoryProps[]
      }

      const isLoaded = !!categorias.find(c => {
        return c.PATH_CATEGORIA === campanhaProduto?.url_amigavel
      })
      const allCategories = [...(isLoaded ? [] : mountCategories(campanhaProduto)), ...categorias]
      const pageBuilder = listPageBuilder.map((i: any, ind: any) => ({ ...i, ID: toCoded(`${ind + 1}`, true, true) }))

      await setOnDB(marcas, 'marcas');
      await setOnDB(precos, 'preco');
      await setOnDB(estoque, 'estoque');
      await setOnDB(imagens, 'imagens');
      await setOnDB(catalogo, 'catalogo');
      await setOnDB(clientes, 'cliente');
      await setOnDB(products, 'produtos');
      await setOnDB(saldoFlex, 'saldo_flex');
      await setOnDB(pageBuilder, 'pagebuilder');
      await setOnDB(indicadores, 'indicadores');
      await setOnDB(allCategories, 'categoria');
      await setOnDB(campanha, 'campanha_indicador');
      await setOnDB(parametrosAws, 'parametros_aws');
      await setOnDB(janelaEntrega, 'janela_entrega');
      await setOnDB(campanhaFaixa, 'campanha_faixa');
      await setOnDB(indicadorLoja, 'indicador_loja');
      await setOnDB(objProduct, 'dicionario_produtos');
      await setOnDB(eventos, 'eventos');
      // await setOnDB(clienteEventos, 'cliente_eventos');
      await setOnDB(campanhaCliente, 'campanha_cliente');
      await setOnDB(campanhaProduto, 'campanha_produto');
      await setOnDB(objCategory, 'dicionario_categorias');
      await setOnDB(promocaoCliente, 'precoPromocaoCliente');
      await setOnDB(campanhaTopMix, 'campanha_mix_fornecedor');
      await setOnDB(parametroPrecoBase, 'parametro_preco_base');
      await setOnDB(recomendacaoBigData, 'recomendacaoBigData');
      await setOnDB(percentualFornecedor, 'percentual_fornecedor');
      await setOnDB(substituicaotributaria, 'cod_regime_tributario');
      await setOnDB(cicloVendaCliente, 'ciclo_venda_cliente_cliente');
      await setOnDB(restricaoProdutoCliente, 'restricaoProdutoCliente');
      await setOnDB(ultimaCompra, 'ultima_compra');
      await setOnDB(clienteEventosCortes, 'cliente_cortes');
      await setOnDB(clienteBoletos, 'cliente_boletos');

      setPreparing(false);
      setIsSuccess(true);
      loading.current = false;
      const rcaCustomer = getOnStorage('rca_usuario');

      setCustomer(rcaCustomer as CustomerDataProps);

      // setAllClients(clientes || []);
      setCampanhaProdutos(campanhaProduto || {});
      setCampanhaClientes(campanhaCliente || {});
      setCampanhaFaixa(campanhaFaixa || []);
      setCampanhaTopMix(campanhaTopMix || []);
      setIndicadorLoja(indicadorLoja || []);
      setIndicadoresProdutividade(indicadores || []);
      return resp
    },
    [fetchDatasets, getOnDB, setDownloadingFileName, setOnDB, setCustomer, setCampanhaProdutos, setCampanhaClientes, setCampanhaFaixa, setCampanhaTopMix, setIndicadorLoja, setIndicadoresProdutividade]
  );

  const updateStock = useCallback(async () => {
    try {
      // console.log("Iniciando verificação de atualização do estoque...");

      const rcaInfos = getOnStorage('rca_usuario');
      if (!rcaInfos?.codPessoa) return;

      const resp: any = await axiosCtx.get(`/getdataset/${rcaInfos.codPessoa}`);
      const [representante] = resp?.data?.representante || [];
      const newList: DataSetsProps[] = representante?.link_Dataset || [];

      if (!newList.length) return;

      const oldList: DataSetsProps[] = getOnStorage('datasets') || [];
      const newStockFile = newList.find(item => item.TIPO_ARQUIVO === 'estoque');
      const oldStockFile = oldList.find(item => item.TIPO_ARQUIVO === 'estoque');

      if (newStockFile && newStockFile.URL_S3 !== oldStockFile?.URL_S3) {
        const stockResponse = await fetchDatasets([newStockFile]);
        
        if (stockResponse?.estoque?.data) {
          // console.log("Atualizando dados de estoque...");
          
          await setOnDB(stockResponse.estoque.data, 'estoque');
          const updatedOldList = oldList.filter(item => item.TIPO_ARQUIVO !== 'estoque');
          const updatedDataSet = [...updatedOldList, newStockFile];
          
          setOnStorage('datasets', updatedDataSet);
          setOnStorage('stock_left', {});
        }
      }
    } catch (error) {
      console.error("Erro ao atualizar o estoque:", error);
      setError(true);
    }
  }, [axiosCtx, fetchDatasets, setOnDB]);
  
  const getLinks = useCallback(async ({isBackground}: {isBackground?: boolean} = {isBackground: false}) => {
    setPreparing(true);
    setError(false);
    setIsSuccess(false);
    
    const rcaInfos = getOnStorage('rca_usuario');
    // Marca que o fluxo de login/sincronização está em andamento apenas quando não é em background
    if (!isBackground) {
      try { localStorage.setItem('login_bootstrap', 'in-progress'); } catch {}
    }
    
    const resp: any = await axiosCtx.get(`/getdataset/${rcaInfos?.codPessoa}`).catch(() => 'error');
    
    if (resp === 'error') {
      setPreparing(false);
      setError(true);
      return;
    }
    
    const [representante] = resp?.data?.representante || [];
    const list = representante?.link_Dataset || [];

    const oldList = getOnStorage('datasets') || []
    const oldListMaped = oldList.map(i => i.URL_S3)
    let filteredList = list.filter((dt: any) => !oldListMaped.includes(dt.URL_S3));

    for (const element of oldList) {
      const fileType = element.TIPO_ARQUIVO
      if (!tableKeys.includes(fileType as Tables)) continue;
      const item = await getOnDB(fileType as Tables) || null

      const isFill = Array.isArray(item) ? !!item?.length : !!Object.keys(item || {})?.length

      if (!isFill) {
        const newElement = list.find((i: any) => i.TIPO_ARQUIVO === element.TIPO_ARQUIVO)
        filteredList.push(newElement)
      }
    }

    const data = await loadFiles(filteredList);
    
    const formattedDate = getDate();
    setLastSync(formattedDate);
    // Concluiu com sucesso o bootstrap de login (download dos datasets necessários)
    if (!isBackground) {
      try { localStorage.setItem('login_bootstrap', 'done'); } catch {}
    }
    
    if(isBackground) return filteredList;

    setTimeout(() => {
      unregisterServiceWorker({});
    }, 100);
    return data
  }, [loadFiles, setLastSync, getDate, axiosCtx]);

  return {
    loading: loading.current,
    hasError,
    noUpdate,
    fileName,
    datasets,
    preparing,
    isSuccess,
    isOpenedModal,

    setError,
    loadFiles,
    getLinks,
    updateStock,
    openModalSync,
  };
};
